import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
function isChildActive(item, activeNavItem) {
    if (!item.children || !activeNavItem)
        return false;
    return item.children.some((c) => c.id === activeNavItem);
}
function navButtonStyle(active, hovered) {
    return {
        background: active
            ? "rgba(255,255,255,0.22)"
            : hovered
                ? "rgba(255,255,255,0.12)"
                : "transparent",
        border: "none",
        borderRadius: 8,
        color: "#fff",
        fontSize: 13,
        fontWeight: active ? 700 : 500,
        padding: "6px 14px",
        cursor: "pointer",
        transition: "background 0.16s",
        height: 34,
    };
}
function NavDropdown({ item, activeNavItem, onNavSelect, }) {
    const [open, setOpen] = useState(false);
    const [hovered, setHovered] = useState(false);
    const wrapRef = useRef(null);
    const childActive = isChildActive(item, activeNavItem);
    const parentActive = open || childActive;
    const close = useCallback(() => {
        setOpen(false);
    }, []);
    useEffect(() => {
        if (!open)
            return;
        function onDoc(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        function onKey(e) {
            if (e.key === "Escape")
                setOpen(false);
        }
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);
    const children = item.children ?? [];
    return (_jsxs("div", { ref: wrapRef, style: { position: "relative" }, children: [_jsxs("button", { type: "button", id: `nav-${item.id}`, "aria-haspopup": "menu", "aria-expanded": open, "aria-controls": `${item.id}-submenu`, onClick: () => {
                    setOpen((o) => !o);
                }, onMouseEnter: () => {
                    setHovered(true);
                }, onMouseLeave: () => {
                    setHovered(false);
                }, style: navButtonStyle(parentActive, hovered && !parentActive), children: [item.label, _jsx("span", { "aria-hidden": "true", style: { marginLeft: 6, opacity: 0.9 }, children: "\u25BE" })] }), open && (_jsx("div", { id: `${item.id}-submenu`, role: "menu", "aria-labelledby": `nav-${item.id}`, style: {
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    marginTop: 4,
                    minWidth: 200,
                    padding: 6,
                    borderRadius: 10,
                    background: "rgba(15, 35, 90, 0.98)",
                    boxShadow: "0 8px 24px rgba(15,23,42,0.35)",
                    zIndex: 200,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                }, children: children.map((child) => {
                    const isActive = activeNavItem === child.id;
                    return (_jsx("button", { type: "button", role: "menuitem", "aria-current": isActive ? "page" : undefined, onClick: () => {
                            onNavSelect?.(child.id);
                            close();
                        }, style: {
                            textAlign: "left",
                            background: isActive ? "rgba(255,255,255,0.18)" : "transparent",
                            border: "none",
                            borderRadius: 6,
                            color: "#fff",
                            fontSize: 13,
                            fontWeight: isActive ? 700 : 500,
                            padding: "8px 12px",
                            cursor: "pointer",
                        }, children: child.label }, child.id));
                }) }))] }));
}
export function GovernanceHeader({ appName, userName, userRole, onMenuToggle, onLogout, navItems, activeNavItem, onNavSelect, rightActions, }) {
    return (_jsxs("header", { style: {
            background: "linear-gradient(135deg, var(--brand-2, #0f3d99), var(--brand, #1d4ed8))",
            color: "#fff",
            padding: "0 24px",
            height: 64,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: "0 2px 12px rgba(15,23,42,0.18)",
            position: "sticky",
            top: 0,
            zIndex: 100,
        }, children: [_jsxs("div", { style: { display: "flex", alignItems: "center", gap: 20 }, children: [_jsxs("div", { style: { display: "flex", alignItems: "center", gap: 14 }, children: [_jsx("div", { "aria-hidden": "true", style: {
                                    width: 36,
                                    height: 36,
                                    borderRadius: 10,
                                    background: "rgba(255,255,255,0.18)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 18,
                                    fontWeight: 900,
                                }, children: "AE" }), _jsx("span", { style: { fontSize: 16, fontWeight: 800, letterSpacing: "-0.01em" }, children: appName })] }), navItems && navItems.length > 0 && (_jsx("nav", { style: { display: "flex", gap: 4, alignItems: "center" }, "aria-label": "Main navigation", children: navItems.map((item) => {
                            if (item.children && item.children.length > 0) {
                                return (_jsx(NavDropdown, { item: item, activeNavItem: activeNavItem, onNavSelect: onNavSelect }, item.id));
                            }
                            return (_jsx(TopLevelNavButton, { label: item.label, active: activeNavItem === item.id, onSelect: () => onNavSelect?.(item.id) }, item.id));
                        }) }))] }), _jsxs("div", { style: { display: "flex", alignItems: "center", gap: 14 }, children: [rightActions, _jsxs("div", { style: { textAlign: "right" }, children: [_jsx("p", { style: { margin: 0, fontSize: 13, fontWeight: 700, color: "#fff" }, children: userName }), _jsx("p", { style: {
                                    margin: 0,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: "rgba(255,255,255,0.7)",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.04em",
                                }, children: userRole })] }), onLogout && (_jsx("button", { onClick: onLogout, "aria-label": "Sign out", style: {
                            background: "rgba(255,255,255,0.15)",
                            border: "none",
                            borderRadius: 8,
                            height: 36,
                            padding: "0 14px",
                            cursor: "pointer",
                            color: "#fff",
                            fontSize: 12,
                            fontWeight: 700,
                            letterSpacing: "0.02em",
                        }, onMouseEnter: (e) => {
                            e.currentTarget.style.background = "rgba(255,255,255,0.25)";
                        }, onMouseLeave: (e) => {
                            e.currentTarget.style.background = "rgba(255,255,255,0.15)";
                        }, children: "Sign out" })), onMenuToggle && (_jsx("button", { onClick: onMenuToggle, "aria-label": "Toggle menu", style: {
                            background: "rgba(255,255,255,0.15)",
                            border: "none",
                            borderRadius: 8,
                            width: 36,
                            height: 36,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontSize: 18,
                        }, children: "\u2630" }))] })] }));
}
function TopLevelNavButton({ label, active, onSelect, }) {
    const [hovered, setHovered] = useState(false);
    return (_jsx("button", { type: "button", onClick: onSelect, "aria-current": active ? "page" : undefined, style: navButtonStyle(active, hovered && !active), onMouseEnter: () => {
            setHovered(true);
        }, onMouseLeave: () => {
            setHovered(false);
        }, children: label }));
}
//# sourceMappingURL=GovernanceHeader.js.map